import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { getBrandBySlug, isProduction } from "@/lib/comparatifs";

export default function ComparatifsVs() {
  const { slug } = useParams<{ slug: string }>();
  const brandSlug = slug?.replace('-vs-cosmos-wave', '');
  const brand = getBrandBySlug(brandSlug || "");

  if (!brand) {
    return <div>Marque non trouvée</div>;
  }

  const canonicalUrl = `https://www.cosmos-wave.com/comparatifs/${brand.slug}-vs-cosmos-wave`;
  const title = `${brand.name} vs Cosmos Wave — ce qui change`;
  const description = `${brand.name} vs Cosmos Wave : comparaison méthodes, stack IA, cas d'usage. Audit 48h, Buy Box 15j, Agent IA 30j. Découvrez les différences clés.`;

  const faqItems = [
    {
      question: `Quelle est la principale différence avec ${brand.name} ?`,
      answer: "Cosmos Wave mise sur l'IA native, le SP-API et une spécialisation Buy Box/Catalog Health, avec des livrables rapides (15-30j) et une approche 100% data-driven."
    },
    {
      question: "Comment se déroule la transition ?",
      answer: "Audit gratuit 48-72h, plan de migration, formation équipes et accompagnement jusqu'à l'autonomie. Pas de lock-in contractuel."
    },
    {
      question: "Quel ROI attendre ?",
      answer: "Stabilisation Buy Box, amélioration TACOS/ROAS, réduction tâches manuelles. Mesurable dès les premières semaines avec notre dashboard temps réel."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList", 
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://www.cosmos-wave.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Comparatifs",
        "item": "https://www.cosmos-wave.com/comparatifs"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": `${brand.name} vs Cosmos Wave`,
        "item": canonicalUrl
      }
    ]
  };

  const professionalServiceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Cosmos Wave", 
    "description": "Agence Amazon & IA spécialisée en Ads SP/SB/SD/DSP, SEO listings & A+, Catalog Health et expansion européenne.",
    "provider": {
      "@type": "Organization",
      "name": "Cosmos Wave",
      "url": "https://www.cosmos-wave.com",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "sales",
        "email": "contact@cosmos-wave.com",
        "areaServed": "FR"
      }
    },
    "areaServed": {
      "@type": "Country",
      "name": "France"
    },
    "serviceType": "Amazon Marketing Services",
    "url": canonicalUrl
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        {!isProduction() && <meta name="robots" content="noindex,nofollow" />}
        
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Cosmos Wave" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://www.cosmos-wave.com/og/comparatifs-vs-1200x630.jpg" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://www.cosmos-wave.com/og/comparatifs-vs-1200x630.jpg" />
        
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(professionalServiceSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        
        
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <header className="mb-16">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                <span className="text-muted-foreground">{brand.name}</span>
                <span className="text-primary mx-4">vs</span>
                <span className="text-foreground">Cosmos Wave</span>
                <span className="block text-2xl md:text-3xl mt-4 text-muted-foreground">
                  Ce qui change
                </span>
              </h1>
              <p className="text-xl text-muted-foreground">
                Comparaison directe des méthodes, stack technique et cas d'usage pour éclairer votre choix d'agence Amazon.
              </p>
            </header>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">Synthèse comparative</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-border rounded-lg">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="border border-border p-4 text-left text-foreground">Aspect</th>
                      <th className="border border-border p-4 text-left text-muted-foreground">{brand.name}</th>
                      <th className="border border-border p-4 text-left text-primary">Cosmos Wave</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-border p-4 font-medium text-foreground">Approche</td>
                      <td className="border border-border p-4 text-muted-foreground">Méthode traditionnelle</td>
                      <td className="border border-border p-4 text-primary font-semibold">IA native + SP-API</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-4 font-medium text-foreground">Stack technique</td>
                      <td className="border border-border p-4 text-muted-foreground">Outils standards</td>
                      <td className="border border-border p-4 text-primary font-semibold">IA propriétaire + SP-API</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-4 font-medium text-foreground">Spécialisation</td>
                      <td className="border border-border p-4 text-muted-foreground">{brand.focus.join(", ")}</td>
                      <td className="border border-border p-4 text-primary font-semibold">Buy Box + Catalog Health</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-4 font-medium text-foreground">Délais audit</td>
                      <td className="border border-border p-4 text-muted-foreground">1-2 semaines</td>
                      <td className="border border-border p-4 text-primary font-semibold">48-72h gratuit</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-4 font-medium text-foreground">Livrables rapides</td>
                      <td className="border border-border p-4 text-muted-foreground">Standard</td>
                      <td className="border border-border p-4 text-primary font-semibold">Sprint 15j + Agent IA 30j</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-4 font-medium text-foreground">Reporting</td>
                      <td className="border border-border p-4 text-muted-foreground">Mensuel</td>
                      <td className="border border-border p-4 text-primary font-semibold">Dashboard temps réel</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-4 font-medium text-foreground">Formation équipes</td>
                      <td className="border border-border p-4 text-muted-foreground">En option</td>
                      <td className="border border-border p-4 text-primary font-semibold">Inclus + documentation</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-4 font-medium text-foreground">Contractuel</td>
                      <td className="border border-border p-4 text-muted-foreground">12-24 mois</td>
                      <td className="border border-border p-4 text-primary font-semibold">Pas de lock-in</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">Méthode & Stack IA</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-card rounded-lg p-6 border border-border">
                  <h3 className="font-semibold text-foreground mb-4">Approche {brand.name}</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Méthodes éprouvées du marché</li>
                    <li>• Outils standards + expertise manuelle</li>
                    <li>• Optimisations régulières</li>
                    <li>• Reporting traditionnel</li>
                  </ul>
                </div>
                <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                  <h3 className="font-semibold text-foreground mb-4">Méthode Cosmos Wave</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• <span className="text-primary font-medium">IA native</span> : ciblage, contenus, anomalies</li>
                    <li>• <span className="text-primary font-medium">SP-API</span> : données temps réel, automation</li>
                    <li>• <span className="text-primary font-medium">Buy Box Focus</span> : variations, prix, stocks</li>
                    <li>• <span className="text-primary font-medium">Dashboard live</span> : KPIs, alertes, actions</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">Cas d'usage & Délais</h2>
              <div className="space-y-6">
                <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                  <h3 className="text-xl font-semibold text-primary mb-3">
                    Audit IA gratuit — 48-72h
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Diagnostic 360° : demande prédictive, SEO listings/A+, Ads performance, Catalog Health & marges. 
                    Plan d'action priorisé avec ROI estimé par action.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full text-sm bg-primary text-primary-foreground">Gratuit</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-primary text-primary-foreground">48-72h</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-primary text-primary-foreground">360°</span>
                  </div>
                </div>

                <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                  <h3 className="text-xl font-semibold text-primary mb-3">
                    Sprint Buy Box — 15 jours
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Stabilisation Buy Box : variations, suppressions, prix, stocks et alertes. 
                    Correctifs documentés et dashboard santé catalogue temps réel.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full text-sm bg-primary text-primary-foreground">15 jours</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-primary text-primary-foreground">Buy Box</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-primary text-primary-foreground">Dashboard</span>
                  </div>
                </div>

                <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                  <h3 className="text-xl font-semibold text-primary mb-3">
                    Agent IA SP-API — 30 jours
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Déploiement agent IA connecté SP-API : SAV/FAQ produits, réponses structurées, 
                    garde-fous brand-safety. Pilote 30 jours avec impact mesurable.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full text-sm bg-primary text-primary-foreground">30 jours</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-primary text-primary-foreground">Agent IA</span>
                    <span className="px-3 py-1 rounded-full text-sm bg-primary text-primary-foreground">SP-API</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">À retenir</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-card rounded-lg p-6 border border-border">
                  <h3 className="font-semibold text-foreground mb-3">Choisir {brand.name} si</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Recherche approche éprouvée</li>
                    <li>• Budget important (15K€+/mois)</li>
                    <li>• Pas d'urgence particulière</li>
                    <li>• Préférence grandes structures</li>
                  </ul>
                </div>
                <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                  <h3 className="font-semibold text-foreground mb-3">Choisir Cosmos Wave si</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• <span className="text-primary font-medium">Problèmes Buy Box/Catalogue</span></li>
                    <li>• <span className="text-primary font-medium">Besoin IA & automation</span></li>
                    <li>• <span className="text-primary font-medium">Résultats rapides (15-30j)</span></li>
                    <li>• <span className="text-primary font-medium">Formation & autonomisation</span></li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">Questions fréquentes</h2>
              <div className="space-y-4">
                {faqItems.map((item, index) => (
                  <div key={index} className="bg-card rounded-lg p-6 border border-border">
                    <h3 className="font-semibold text-foreground mb-3">{item.question}</h3>
                    <p className="text-muted-foreground">{item.answer}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="text-center">
              <div className="bg-primary/5 rounded-lg p-8">
                <h2 className="text-2xl font-semibold mb-4 text-foreground">
                  Évaluez votre situation en 48-72h
                </h2>
                <p className="text-muted-foreground mb-6">
                  Diagnostic gratuit avec comparaison personnalisée selon vos besoins Amazon actuels.
                </p>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Demander une évaluation
                </Link>
              </div>
            </section>

            <footer className="text-xs text-muted-foreground pt-8 mt-8 border-t border-border">
              <p>
                {brand.name} est une marque de son propriétaire. Page non affiliée ; comparaison impartiale.
              </p>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
}